import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiGithub, mdiLinkedin, mdiEmail } from '@mdi/js';
import { Container } from '../ui/Container';

const FooterWrapper = styled.footer`
  background: var(--color-background-secondary);
  border-top: 1px solid var(--color-border);
  padding: var(--space-16) 0 var(--space-8);
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: var(--space-12);

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-8);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BrandColumn = styled.div`
  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-weight: var(--font-bold);
  font-size: var(--text-lg);
  color: var(--color-text-primary);
  text-decoration: none;
  margin-bottom: var(--space-4);

  &:hover {
    color: var(--color-text-primary);
  }
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: var(--font-bold);
  font-size: var(--text-sm);
`;

const BrandDescription = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  max-width: 280px;
  margin-bottom: var(--space-6);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--space-3);
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--color-border);
  color: var(--color-text-secondary);
  transition: background var(--transition-fast), color var(--transition-fast);

  &:hover {
    background: var(--color-brand-primary);
    color: white;
  }
`;

const LinkColumn = styled.div``;

const ColumnTitle = styled.h4`
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const FooterLink = styled(Link)`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-brand-primary);
  }
`;

const ExternalLink = styled.a`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-brand-primary);
  }
`;

const BottomBar = styled.div`
  margin-top: var(--space-12);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
`;

const Copyright = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const DBANote = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-tertiary);
`;

const LegalLinks = styled.div`
  display: flex;
  gap: var(--space-6);
`;

const LegalLink = styled(Link)`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-text-secondary);
  }
`;

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <Container>
        <FooterContent>
          <BrandColumn>
            <Logo to="/">
              <LogoIcon>LSA</LogoIcon>
              Lancaster Systems Automation
            </Logo>
            <BrandDescription>
              Crafting innovative apps and custom software solutions for businesses and individuals.
            </BrandDescription>
            <SocialLinks>
              <SocialLink
                href="https://github.com/isaaclong26"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <Icon path={mdiGithub} size={0.9} />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/in/isaac-long-403773222/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Icon path={mdiLinkedin} size={0.9} />
              </SocialLink>
              <SocialLink
                href="mailto:sales@lancastersystemsautomation.com"
                aria-label="Email"
              >
                <Icon path={mdiEmail} size={0.9} />
              </SocialLink>
            </SocialLinks>
          </BrandColumn>

          <LinkColumn>
            <ColumnTitle>Products</ColumnTitle>
            <LinkList>
              <li><FooterLink to="/products/charque">Charque</FooterLink></li>
              <li><FooterLink to="/products/what">What</FooterLink></li>
              <li><FooterLink to="/products/when">When</FooterLink></li>
              <li><FooterLink to="/products/bestagon">The Bestagon</FooterLink></li>
            </LinkList>
          </LinkColumn>

          <LinkColumn>
            <ColumnTitle>Company</ColumnTitle>
            <LinkList>
              <li><FooterLink to="/about">About</FooterLink></li>
              <li><FooterLink to="/services">Services</FooterLink></li>
              <li><FooterLink to="/portfolio">Portfolio</FooterLink></li>
              <li><FooterLink to="/contact">Contact</FooterLink></li>
            </LinkList>
          </LinkColumn>

          <LinkColumn>
            <ColumnTitle>Resources</ColumnTitle>
            <LinkList>
              <li><ExternalLink href="#">Documentation</ExternalLink></li>
              <li><ExternalLink href="#">Support</ExternalLink></li>
              <li><FooterLink to="/contact">Hire Us</FooterLink></li>
            </LinkList>
          </LinkColumn>
        </FooterContent>

        <BottomBar>
          <Copyright>
            &copy; {currentYear} Lancaster Systems Automation.{' '}
            <DBANote>A DBA of Data 26 Solutions LLC</DBANote>
          </Copyright>
          <LegalLinks>
            <LegalLink to="/privacy">Privacy</LegalLink>
            <LegalLink to="/terms">Terms</LegalLink>
          </LegalLinks>
        </BottomBar>
      </Container>
    </FooterWrapper>
  );
}
